import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Send, Eye, CheckCircle2, Clock, History } from "lucide-react";
import { API } from "@/contexts/AuthContext";

const FOHAuditPanel = () => {
  const { yestimateId } = useParams();
  const [audit, setAudit] = useState(null);
  const [yestimateData, setYestimateData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [creatingVersion, setCreatingVersion] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    Promise.all([
      axios.get(`${API}/foh/yestimate/${yestimateId}/audit`),
      axios.get(`${API}/foh/yestimate/${yestimateId}`)
    ]).then(([auditRes, yestimateRes]) => {
      setAudit(auditRes.data);
      setYestimateData(yestimateRes.data);
    }).finally(() => setLoading(false));
  }, [yestimateId]);

  const handleNewVersion = async () => {
    setCreatingVersion(true);
    try {
      const res = await axios.post(`${API}/foh/yestimate/${yestimateId}/new-version`);
      navigate(`/app/foh/yestimate/${res.data.id}/pricing`);
    } catch (err) {
      alert(err.response?.data?.detail || "Failed to create new version");
    } finally {
      setCreatingVersion(false);
    }
  };

  if (loading) return <div className="p-8 text-center">Loading...</div>;
  if (!audit) return <div className="p-8 text-center">Not found</div>;

  const getStatusIcon = (status) => {
    switch(status) {
      case "sent": return <Send className="w-4 h-4 text-blue-500" />;
      case "viewed": return <Eye className="w-4 h-4 text-purple-500" />;
      case "authorised": return <CheckCircle2 className="w-4 h-4 text-green-500" />;
      default: return <Clock className="w-4 h-4 text-slate-400" />;
    }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-white border-b px-6 py-4">
        <div className="max-w-3xl mx-auto">
          <Button variant="ghost" size="sm" onClick={() => navigate("/app/foh")} className="mb-2">
            ← Back to Queue
          </Button>
          <div className="flex justify-between items-start">
            <div>
              <h1 className="font-bold text-xl text-slate-800">Status & Audit</h1>
              <p className="text-slate-500">{yestimateData?.job?.reg} • Version {audit.version}</p>
            </div>
            <Badge className={
              audit.status === "authorised" ? "bg-green-100 text-green-700" :
              audit.status === "viewed" ? "bg-purple-100 text-purple-700" :
              audit.status === "sent" ? "bg-blue-100 text-blue-700" :
              "bg-slate-100 text-slate-700"
            }>
              {audit.status}
            </Badge>
          </div>
        </div>
      </header>

      <div className="max-w-3xl mx-auto p-6">
        {/* Status Cards */}
        <div className="grid grid-cols-4 gap-4 mb-6">
          <Card className={audit.sent_at ? "bg-blue-50 border-blue-200" : ""}>
            <CardContent className="p-4 text-center">
              <Send className={`w-5 h-5 mx-auto mb-2 ${audit.sent_at ? "text-blue-500" : "text-slate-300"}`} />
              <p className="text-xs font-medium">Sent</p>
              {audit.sent_at && <p className="text-xs text-slate-500 mt-1">{new Date(audit.sent_at).toLocaleString()}</p>}
            </CardContent>
          </Card>
          <Card className={audit.viewed_at ? "bg-purple-50 border-purple-200" : ""}>
            <CardContent className="p-4 text-center">
              <Eye className={`w-5 h-5 mx-auto mb-2 ${audit.viewed_at ? "text-purple-500" : "text-slate-300"}`} />
              <p className="text-xs font-medium">Viewed</p>
              {audit.viewed_at && <p className="text-xs text-slate-500 mt-1">{new Date(audit.viewed_at).toLocaleString()}</p>}
            </CardContent>
          </Card>
          <Card className={audit.authorised_at ? "bg-green-50 border-green-200" : ""}>
            <CardContent className="p-4 text-center">
              <CheckCircle2 className={`w-5 h-5 mx-auto mb-2 ${audit.authorised_at ? "text-green-500" : "text-slate-300"}`} />
              <p className="text-xs font-medium">Authorised</p>
              {audit.authorised_at && <p className="text-xs text-slate-500 mt-1">{new Date(audit.authorised_at).toLocaleString()}</p>}
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 text-center">
              <History className="w-5 h-5 mx-auto mb-2 text-slate-500" />
              <p className="text-xs font-medium">Version</p>
              <p className="text-lg font-bold">{audit.version}</p>
            </CardContent>
          </Card>
        </div>

        {/* Details */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="text-base">Details</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="flex justify-between">
              <span className="text-slate-500">Delivery Method</span>
              <span className="font-medium">{audit.delivery_method || "—"}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-slate-500">Tone</span>
              <span className="font-medium capitalize">{audit.tone}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-slate-500">Total</span>
              <span className="font-medium">£{yestimateData?.yestimate?.totals?.total?.toFixed(2) || "0.00"}</span>
            </div>
          </CardContent>
        </Card>

        {/* Audit Log */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="text-base">Audit Trail</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {audit.audit_log?.map((entry, idx) => (
                <div key={idx} className="flex items-start gap-3 text-sm">
                  <div className="mt-1">{getStatusIcon(entry.action)}</div>
                  <div>
                    <p className="font-medium capitalize">{entry.action.replace("_", " ")}</p>
                    <p className="text-slate-500">{new Date(entry.timestamp).toLocaleString()}</p>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Actions */}
        {audit.status !== "draft" && (
          <Button 
            variant="outline" 
            className="w-full"
            onClick={handleNewVersion}
            disabled={creatingVersion}
            data-testid="create-new-version-btn"
          >
            {creatingVersion ? "Creating..." : "Create New Version (v" + (audit.version + 1) + ")"}
          </Button>
        )}
      </div>
    </div>
  );
};

export default FOHAuditPanel;
